// Identity keys: Ed25519 for signatures, X25519 for encryption (docs/protocol.md, "Identities and keys").
import { b64u, buf, concat, fromB64u, utf8 } from "./encoding.js";
const subtle = () => globalThis.crypto.subtle;
/**
 * New identity. Devices pass extractable=false so private keys can never leave IndexedDB.
 * The daemon passes true so it can persist them (protected with DPAPI).
 */
export async function generateIdentity(extractable) {
    const sig = (await subtle().generateKey({ name: "Ed25519" }, extractable, ["sign", "verify"]));
    const enc = (await subtle().generateKey({ name: "X25519" }, extractable, ["deriveBits"]));
    return { sig, enc };
}
export async function publicIdentity(id) {
    return {
        sigPub: b64u(await subtle().exportKey("raw", id.sig.publicKey)),
        encPub: b64u(await subtle().exportKey("raw", id.enc.publicKey)),
    };
}
export const importSigPub = (sigPub) => subtle().importKey("raw", buf(fromB64u(sigPub)), { name: "Ed25519" }, true, ["verify"]);
export const importEncPub = (encPub) => subtle().importKey("raw", buf(fromB64u(encPub)), { name: "X25519" }, true, []);
async function sha256(bytes) {
    return new Uint8Array(await subtle().digest("SHA-256", buf(bytes)));
}
/** machineId / deviceId: first 16 bytes of SHA-256(raw Ed25519 public key), base64url (22 chars). */
export async function idFor(sigPub) {
    return b64u((await sha256(fromB64u(sigPub))).slice(0, 16));
}
/** Fingerprint of a party: first 16 bytes of SHA-256(sigPub || encPub), base64url. */
export async function fingerprint(pub) {
    return b64u((await sha256(concat(fromB64u(pub.sigPub), fromB64u(pub.encPub)))).slice(0, 16));
}
export async function sign(key, data) {
    return b64u(await subtle().sign({ name: "Ed25519" }, key, buf(data)));
}
export async function verify(key, sig, data) {
    try {
        return await subtle().verify({ name: "Ed25519" }, key, buf(fromB64u(sig)), buf(data));
    }
    catch {
        return false;
    }
}
export const signText = (key, text) => sign(key, utf8(text));
export const verifyText = (key, sig, text) => verify(key, sig, utf8(text));
export async function exportIdentity(id) {
    const pub = await publicIdentity(id);
    return {
        sigPriv: b64u(await subtle().exportKey("pkcs8", id.sig.privateKey)),
        encPriv: b64u(await subtle().exportKey("pkcs8", id.enc.privateKey)),
        ...pub,
    };
}
export async function importIdentity(s) {
    const sigPriv = await subtle().importKey("pkcs8", buf(fromB64u(s.sigPriv)), { name: "Ed25519" }, true, ["sign"]);
    const encPriv = await subtle().importKey("pkcs8", buf(fromB64u(s.encPriv)), { name: "X25519" }, true, ["deriveBits"]);
    return {
        sig: { privateKey: sigPriv, publicKey: await importSigPub(s.sigPub) },
        enc: { privateKey: encPriv, publicKey: await importEncPub(s.encPub) },
    };
}
//# sourceMappingURL=keys.js.map